// src/app/pages/admin/AssetDrawer.jsx
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { adminAssets } from "../../../services/adminApi";
import { measureGLB } from "../../../utils/measureGLB";
import { X, Upload, Image as ImageIcon, Save, AlertCircle, CheckCircle, Info, RotateCcw, Move3D, Ruler, Settings } from "lucide-react";
import { FaCube } from "react-icons/fa6";

const T = {
  dark: "#0c1118", amber: "#111111", bg: "#f4f6f8",
  surface: "#ffffff", border: "#e8ecf0", muted: "#6b7280",
};

if (typeof document !== "undefined" && !document.getElementById("ad-styles")) {
  const s = document.createElement("style");
  s.id = "ad-styles";
  s.textContent = `
    @keyframes ad-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .ad-drop:hover { border-color: #111111 !important; background: rgba(0,0,0,0.02) !important; }
    .ad-toggle:hover { background: #f4f6f8 !important; }
    .ad-step-btn:hover { opacity: 0.8; }
  `;
  document.head.appendChild(s);
}

const categories = ["person", "vehicle", "tree", "furniture", "decor", "generic"];
const placements = ["ground", "wall", "ceiling", "free"];
const pivots = ["foot", "center", "custom"];

const INP = { border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif", outline: "none" };
const INP_MONO = { ...INP, fontFamily: "'DM Sans', sans-serif" };

export default function AssetDrawer({ id, onClose }) {
  const isEdit = !!id;
  const [busy, setBusy] = useState(false);
  const [mErr, setMErr] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const [form, setForm] = useState({
    code: "", name: "", category: "generic", placement: "ground",
    allowRotate: true, allowScale: true,
    targetHeightM: "", targetWidthM: "", targetDepthM: "",
    nativeHeightM: "", nativeWidthM: "", nativeDepthM: "",
    pivot: "foot", pivotOffsetY: "", minScale: "", maxScale: "",
    notes: "", mesh: null, thumbnail: null,
  });

  const [previewUrls, setPreviewUrls] = useState({ mesh: null, thumbnail: null });

  useEffect(() => {
    (async () => {
      if (!isEdit) return;
      try {
        const a = await adminAssets.get(id);
        setForm({
          code: a.code || "", name: a.name || "", category: a.category || "generic",
          placement: a.placement || "ground", allowRotate: a.allowRotate ?? true,
          allowScale: a.allowScale ?? true, targetHeightM: a.target?.h ?? "",
          targetWidthM: a.target?.w ?? "", targetDepthM: a.target?.d ?? "",
          nativeHeightM: a.native?.h ?? "", nativeWidthM: a.native?.w ?? "",
          nativeDepthM: a.native?.d ?? "", pivot: a.pivot?.type || "foot",
          pivotOffsetY: a.pivot?.y ?? "", minScale: a.minScale ?? "",
          maxScale: a.maxScale ?? "", notes: a.notes || "", mesh: null, thumbnail: null,
        });
      } catch (e) { console.error("Failed to load asset:", e); }
    })();
  }, [id, isEdit]);

  async function onMeshPicked(file) {
    if (!file) return;
    try {
      setMErr(""); setBusy(true);
      const m = await measureGLB(file);
      setForm((f) => ({
        ...f, mesh: file,
        nativeWidthM: m.widthM.toFixed(3), nativeHeightM: m.heightM.toFixed(3), nativeDepthM: m.depthM.toFixed(3),
        targetHeightM: f.category === "person" ? "1.75" : f.category === "vehicle" ? "1.5" : f.category === "tree" ? "5.0" : f.targetHeightM,
      }));
      setPreviewUrls((prev) => ({ ...prev, mesh: URL.createObjectURL(file) }));
    } catch (e) {
      console.error(e);
      setMErr("Could not measure GLB file. Please ensure it's a valid GLB/GLTF file.");
    } finally { setBusy(false); }
  }

  function onThumbnailPicked(file) {
    if (!file) return;
    setForm((f) => ({ ...f, thumbnail: file }));
    setPreviewUrls((prev) => ({ ...prev, thumbnail: URL.createObjectURL(file) }));
  }

  async function submit() {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") return;
        if (k === "mesh" || k === "thumbnail") fd.append(k, v);
        else fd.append(k, String(v));
      });
      if (isEdit) await adminAssets.update(id, fd);
      else await adminAssets.create(fd);
      onClose(true);
    } catch (e) {
      console.error(e);
      alert(e.message || "Save failed");
    } finally { setBusy(false); }
  }

  const canProceedToStep2 = form.name && form.code && form.category;
  const canProceedToStep3 = canProceedToStep2 && (form.mesh || isEdit);
  const canSave = canProceedToStep3 && form.nativeHeightM && form.nativeWidthM && form.nativeDepthM;

  const steps = [
    { number: 1, title: "Basic Info" },
    { number: 2, title: "Files" },
    { number: 3, title: "Dimensions" },
    { number: 4, title: "Settings" },
  ];

  const canNext =
    (currentStep === 1 && canProceedToStep2) ||
    (currentStep === 2 && canProceedToStep3) ||
    currentStep >= 3;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 50 }}>
      <div style={{ position: "absolute", right: 0, top: 0, height: "100%", width: "100%", maxWidth: 600, background: T.surface, display: "flex", flexDirection: "column", boxShadow: "-4px 0 40px rgba(0,0,0,0.15)", fontFamily: "'DM Sans', sans-serif" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, background: T.dark, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{isEdit ? "Edit Asset" : "New 3D Asset"}</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
              {isEdit ? "Update asset properties and files" : "Add a new 3D model to your library"}
            </p>
          </div>
          <button onClick={() => onClose(false)} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8, cursor: "pointer", padding: 8, color: "#fff" }}>
            <X size={18} />
          </button>
        </div>

        {/* Stepper */}
        <div style={{ padding: "14px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {steps.map((step, i) => (
            <div key={step.number} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="ad-step-btn" onClick={() => setCurrentStep(step.number)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: currentStep === step.number ? T.amber : currentStep > step.number ? "#16a34a" : T.border, color: currentStep === step.number || currentStep > step.number ? "#fff" : T.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {currentStep > step.number ? <CheckCircle size={14} /> : step.number}
              </button>
              <span style={{ fontSize: 12, fontWeight: currentStep === step.number ? 700 : 400, color: currentStep >= step.number ? T.dark : T.muted }}>{step.title}</span>
              {i < steps.length - 1 && <div style={{ width: 24, height: 2, background: currentStep > step.number ? "#16a34a" : T.border, borderRadius: 1 }} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Info} title="Basic Information" description="Define the core properties of your 3D asset" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <FormField label="Asset Code" required>
                  <input style={INP_MONO} placeholder="e.g., CHAIR_001" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </FormField>
                <FormField label="Display Name" required>
                  <input style={INP} placeholder="e.g., Modern Office Chair" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </FormField>
                <FormField label="Category" required>
                  <select style={INP} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {categories.map((cat) => <option key={cat} value={cat} style={{ textTransform: "capitalize" }}>{cat}</option>)}
                  </select>
                </FormField>
                <FormField label="Placement">
                  <select style={INP} value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })}>
                    {placements.map((p) => <option key={p} value={p} style={{ textTransform: "capitalize" }}>{p}</option>)}
                  </select>
                </FormField>
              </div>
              <FormField label="Description / Notes">
                <textarea style={{ ...INP, resize: "vertical" }} rows={3} placeholder="Optional notes about this asset..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </FormField>
            </div>
          )}

          {/* Step 2: Files */}
          {currentStep === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Upload} title="Upload Files" description="Add your 3D model and thumbnail image" />
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  3D Model (.glb/.gltf) {!isEdit && <span style={{ color: "#dc2626" }}>*</span>}
                </label>
                <FileUploadZone accept=".glb,.gltf" onFileSelect={onMeshPicked} previewUrl={previewUrls.mesh} Icon={FaCube} title="Drop your 3D model here" subtitle="GLB or GLTF format, max 50MB" busy={busy} />
                {mErr && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, marginTop: 10 }}>
                    <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 13, color: "#7f1d1d" }}>{mErr}</span>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Thumbnail Image (.jpg/.png)</label>
                <FileUploadZone accept="image/*" onFileSelect={onThumbnailPicked} previewUrl={previewUrls.thumbnail} Icon={ImageIcon} title="Drop your thumbnail here" subtitle="PNG or JPG format, recommended 512×512px" isImage />
              </div>
            </div>
          )}

          {/* Step 3: Dimensions */}
          {currentStep === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Ruler} title="Dimensions & Scale" description="Configure size properties and scaling behavior" />
              {/* Native */}
              <div style={{ padding: 16, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
                <div style={{ fontWeight: 700, color: "#1e3a8a", marginBottom: 4 }}>Native Dimensions (meters)</div>
                <div style={{ fontSize: 12, color: "#3b82f6", marginBottom: 12 }}>Automatically detected from your 3D model</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                  {["nativeWidthM", "nativeHeightM", "nativeDepthM"].map((key, i) => (
                    <FormField key={key} label={["Width", "Height", "Depth"][i]}>
                      <input style={{ ...INP_MONO, fontSize: 12, border: "1px solid #bfdbfe" }} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} readOnly={!form[key]} />
                    </FormField>
                  ))}
                </div>
              </div>
              {/* Target */}
              <div style={{ padding: 16, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10 }}>
                <div style={{ fontWeight: 700, color: "#14532d", marginBottom: 4 }}>Target Dimensions (feet)</div>
                <div style={{ fontSize: 12, color: "#16a34a", marginBottom: 12 }}>Desired real-world size when placed in scene</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                  {["targetWidthM", "targetHeightM", "targetDepthM"].map((key, i) => (
                    <FormField key={key} label={["Width", "Height", "Depth"][i]}>
                      <input style={{ ...INP_MONO, fontSize: 12, border: "1px solid #bbf7d0" }} placeholder="Auto" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                    </FormField>
                  ))}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <FormField label="Minimum Scale">
                  <input style={INP_MONO} placeholder="0.1" value={form.minScale} onChange={(e) => setForm({ ...form, minScale: e.target.value })} />
                </FormField>
                <FormField label="Maximum Scale">
                  <input style={INP_MONO} placeholder="10.0" value={form.maxScale} onChange={(e) => setForm({ ...form, maxScale: e.target.value })} />
                </FormField>
              </div>
            </div>
          )}

          {/* Step 4: Settings */}
          {currentStep === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Settings} title="Behavior Settings" description="Configure how users can interact with this asset" />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>User Permissions</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <ToggleField label="Allow Rotation" description="Users can rotate this asset in the scene" Icon={RotateCcw} checked={form.allowRotate} onChange={(v) => setForm({ ...form, allowRotate: v })} />
                  <ToggleField label="Allow Scaling" description="Users can resize this asset" Icon={Move3D} checked={form.allowScale} onChange={(v) => setForm({ ...form, allowScale: v })} />
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Pivot Settings</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <FormField label="Pivot Type">
                    <select style={INP} value={form.pivot} onChange={(e) => setForm({ ...form, pivot: e.target.value })}>
                      {pivots.map((p) => <option key={p} value={p} style={{ textTransform: "capitalize" }}>{p}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Pivot Offset Y">
                    <input style={INP_MONO} placeholder="0.0" value={form.pivotOffsetY} onChange={(e) => setForm({ ...form, pivotOffsetY: e.target.value })} />
                  </FormField>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            {currentStep > 1 && (
              <button onClick={() => setCurrentStep(currentStep - 1)} style={{ padding: "8px 16px", border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>Previous</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => onClose(false)} style={{ padding: "8px 16px", border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>Cancel</button>
            {currentStep < 4 ? (
              <button onClick={() => setCurrentStep(currentStep + 1)} disabled={!canNext} style={{ padding: "8px 18px", background: canNext ? T.amber : T.border, color: canNext ? "#ffffff" : T.muted, border: "none", borderRadius: 8, cursor: canNext ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700 }}>
                Continue
              </button>
            ) : (
              <button onClick={submit} disabled={!canSave || busy} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 20px", background: canSave ? "#16a34a" : T.border, color: canSave ? "#fff" : T.muted, border: "none", borderRadius: 8, cursor: canSave && !busy ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
                {busy ? <div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "ad-spin 0.8s linear infinite" }} /> : <Save size={15} />}
                {busy ? "Saving..." : "Save Asset"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function StepHeader({ Icon, title, description }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, paddingBottom: 16, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ width: 44, height: 44, background: `${T.amber}22`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={22} color={T.amber} />
      </div>
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h3>
        <p style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>{description}</p>
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>
        {label}{required && <span style={{ color: "#dc2626", marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function ToggleField({ label, description, Icon, checked, onChange }) {
  return (
    <div className="ad-toggle" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
      <div style={{ width: 38, height: 38, background: T.bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} color={T.muted} />
      </div>
      <div style={{ flex: 1 }}>
        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
            <div style={{ fontSize: 12, color: T.muted }}>{description}</div>
          </div>
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 15, height: 15 }} />
        </label>
      </div>
    </div>
  );
}

function FileUploadZone({ accept, onFileSelect, previewUrl, Icon, title, subtitle, busy, isImage }) {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) onFileSelect(e.dataTransfer.files[0]);
  };

  return (
    <div className="ad-drop" style={{ position: "relative", border: `2px dashed ${dragActive ? T.amber : T.border}`, borderRadius: 10, padding: 24, textAlign: "center", background: dragActive ? "rgba(0,0,0,0.02)" : T.bg, transition: "all 0.15s" }} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
      <input type="file" accept={accept} onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} disabled={busy} />
      {previewUrl && isImage ? (
        <div>
          <img src={previewUrl} alt="Preview" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, margin: "0 auto 8px" }} />
          <p style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>Image selected</p>
        </div>
      ) : previewUrl ? (
        <div>
          <FaCube style={{ margin: "0 auto 8px", color: "#16a34a", display: "block" }} size={40} />
          <p style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>3D model selected</p>
        </div>
      ) : (
        <div>
          <Icon size={40} color={dragActive ? T.amber : T.border} style={{ margin: "0 auto 12px", display: "block" }} />
          <p style={{ fontSize: 14, fontWeight: 600, color: dragActive ? T.dark : T.dark, marginBottom: 4 }}>{dragActive ? "Drop file here" : title}</p>
          <p style={{ fontSize: 12, color: T.muted }}>{subtitle}</p>
          {busy && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10 }}>
              <div style={{ width: 14, height: 14, border: `2px solid ${T.amber}`, borderTopColor: "transparent", borderRadius: "50%", animation: "ad-spin 0.8s linear infinite" }} />
              <span style={{ fontSize: 13, color: T.amber }}>Processing...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
